import { useEffect, useMemo, useState } from 'react';
import { UserGroup } from '../../apis/api-client';
import { groupsClient } from '../../apis/client-instances';
import PageHeader from '../common/PageHeader';
import { EmptyState, SectionHeader } from '../common';
import ComplianceDashboard from '../reporting/ComplianceDashboard';
import { GroupIcon, PersonIcon, BarChartIcon } from '../icons';
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '../ui';
import { useOwnedGroups } from '../../hooks/useOwnedGroups';

/**
 * MyGroups — the learner-facing "group admin" surface.
 *
 * A learner who owns one or more groups (the group's designated admin) uses
 * this to view their group's roster and track its members' course progress.
 * Strictly read-only: no member management, no settings. It reuses the admin
 * ComplianceDashboard in owner mode; the server independently scopes a
 * learner-owner's reporting to their owned groups.
 *
 * Rendered by GroupsPage for `UserRole.User` — admins get the full
 * GroupsDirectory at the same route instead.
 */
export default function MyGroups() {
  const { ownedGroups, loading, isGroupAdmin } = useOwnedGroups();

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [members, setMembers] = useState<UserGroup[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Default the roster selector to the first owned group once they load.
  useEffect(() => {
    if (selectedGroupId == null && ownedGroups.length > 0) {
      setSelectedGroupId(ownedGroups[0].id ?? null);
    }
  }, [ownedGroups, selectedGroupId]);

  useEffect(() => {
    if (selectedGroupId == null) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    setMembersLoading(true);
    groupsClient
      .getGroupMembers(selectedGroupId)
      .then((rows) => {
        if (!cancelled) setMembers(rows ?? []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId]);

  const selectedGroup = useMemo(
    () => ownedGroups.find((g) => g.id === selectedGroupId),
    [ownedGroups, selectedGroupId],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isGroupAdmin) {
    return (
      <EmptyState
        icon={<GroupIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="You don't manage any groups"
        subtitle="This page is available to learners who have been made the admin of a group."
      />
    );
  }

  return (
    <Box>
      <PageHeader
        icon={<GroupIcon />}
        title="My Groups"
        subtitle="View your group's members and track their course progress."
      />

      <SectionHeader
        variant="accent"
        icon={<PersonIcon />}
        title="Members"
        subtitle={
          selectedGroup ? `${members.length} member${members.length === 1 ? '' : 's'} in ${selectedGroup.name}` : undefined
        }
        action={
          ownedGroups.length > 1 ? (
            <Select
              size="small"
              value={selectedGroupId ?? ''}
              onChange={(e) => setSelectedGroupId(Number(e.target.value))}
              sx={{ minWidth: 200 }}
            >
              {ownedGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          ) : undefined
        }
      />

      {membersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : members.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>
          This group has no members yet.
        </Typography>
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.userId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {[m.userLastName, m.userFirstName].filter(Boolean).join(', ') || '—'}
                      </Typography>
                      {m.isOwner && <Chip label="Group admin" size="small" color="primary" variant="outlined" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{m.userEmail}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SectionHeader
        variant="accent"
        icon={<BarChartIcon />}
        title="Course progress"
        subtitle="Compliance across your groups' members. Use the Groups filter to narrow to a single group."
      />
      <ComplianceDashboard ownedGroups={ownedGroups} />
    </Box>
  );
}
